import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { ScrollArea } from "./ui/scroll-area";
import { toast } from "sonner";
import { format, isToday, isYesterday } from "date-fns";
import { 
  MessageCircle, 
  X, 
  Minimize2,
  Maximize2,
  Send, 
  Users,
  Hash,
  ChevronLeft,
  Bell,
  BellOff,
  Circle,
  Check,
  CheckCheck
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Get WebSocket URL from API URL
const getWsUrl = () => {
  const apiUrl = process.env.REACT_APP_BACKEND_URL || "";
  return apiUrl.replace("https://", "wss://").replace("http://", "ws://");
};

export default function ChatWidget({ user }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [showRoomList, setShowRoomList] = useState(true);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [typingUsers, setTypingUsers] = useState({});
  const [wsConnected, setWsConnected] = useState(false);
  
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Connect to WebSocket
  const connectWebSocket = useCallback(() => {
    if (!user?.user_id || !user?.active_org_id) return;
    
    const wsUrl = `${getWsUrl()}/ws/chat/${user.active_org_id}/${user.user_id}`;
    
    try {
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onopen = () => {
        console.log("WebSocket connected");
        setWsConnected(true);
        // Clear any reconnect timeout
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
      };
      
      wsRef.current.onmessage = (event) => {
        const data = JSON.parse(event.data);
        handleWebSocketMessage(data);
      };
      
      wsRef.current.onclose = () => {
        console.log("WebSocket disconnected");
        setWsConnected(false);
        // Try to reconnect after 3 seconds
        reconnectTimeoutRef.current = setTimeout(() => {
          if (isOpen) connectWebSocket();
        }, 3000);
      };
      
      wsRef.current.onerror = (error) => {
        console.error("WebSocket error:", error);
      };
    } catch (error) {
      console.error("WebSocket connection failed:", error);
    }
  }, [user?.user_id, user?.active_org_id, isOpen]);

  // Handle incoming WebSocket messages
  const handleWebSocketMessage = (data) => {
    switch (data.type) {
      case "online_users":
        setOnlineUsers(data.users || []);
        break;
        
      case "user_status":
        if (data.status === "online") {
          setOnlineUsers(prev => [...new Set([...prev, data.user_id])]);
        } else {
          setOnlineUsers(prev => prev.filter(id => id !== data.user_id));
        }
        break;
        
      case "new_message":
        // Add message if we're in the same room
        if (selectedRoom?.room_id === data.message.room_id) {
          setMessages(prev => {
            // Avoid duplicates
            if (prev.some(m => m.message_id === data.message.message_id)) {
              return prev;
            }
            return [...prev, data.message];
          });
          
          // Mark as read if message is from someone else
          if (data.message.sender_id !== user?.user_id) {
            markMessagesAsRead(data.message.room_id);
          }
        } else {
          // Increment unread count for other rooms
          if (data.message.sender_id !== user?.user_id) {
            setUnreadCount(prev => prev + 1);
            
            // Play notification sound
            if (notificationsEnabled) {
              playNotificationSound();
            }
          }
        }
        
        // Update room last message in list
        setRooms(prev => prev.map(room => {
          if (room.room_id === data.message.room_id) {
            return {
              ...room,
              last_message: data.message.content,
              last_message_at: data.message.sent_at,
              unread_count: room.room_id === selectedRoom?.room_id 
                ? 0 
                : (room.unread_count || 0) + (data.message.sender_id !== user?.user_id ? 1 : 0)
            };
          }
          return room;
        }));
        break;
        
      case "messages_read":
        // Update read receipts
        if (selectedRoom?.room_id === data.room_id) {
          setMessages(prev => prev.map(msg => ({
            ...msg,
            read_by: [...(msg.read_by || []), data.read_by]
          })));
        }
        break;
        
      case "typing":
        if (data.room_id === selectedRoom?.room_id && data.user_id !== user?.user_id) {
          if (data.is_typing) {
            setTypingUsers(prev => ({ ...prev, [data.user_id]: true }));
            // Clear typing indicator after 3 seconds
            setTimeout(() => {
              setTypingUsers(prev => {
                const newState = { ...prev };
                delete newState[data.user_id];
                return newState;
              });
            }, 3000);
          } else {
            setTypingUsers(prev => {
              const newState = { ...prev };
              delete newState[data.user_id];
              return newState;
            });
          }
        }
        break;
    }
  };

  // Send message via WebSocket
  const sendMessage = () => {
    if (!newMessage.trim() || !selectedRoom || !wsRef.current) return;
    
    if (wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: "message",
        room_id: selectedRoom.room_id,
        content: newMessage.trim()
      }));
      setNewMessage("");
    } else {
      // Fallback to REST API if WebSocket is not connected
      sendMessageViaRest();
    }
  };

  // Fallback REST API for sending messages
  const sendMessageViaRest = async () => {
    if (!newMessage.trim() || !selectedRoom) return;
    
    try {
      const response = await fetch(`${API}/chat/rooms/${selectedRoom.room_id}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ content: newMessage })
      });
      
      if (response.ok) {
        setNewMessage("");
        fetchMessages(selectedRoom.room_id);
      }
    } catch (error) {
      toast.error("Failed to send message");
    }
  };

  // Mark messages as read
  const markMessagesAsRead = (roomId) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: "mark_read",
        room_id: roomId
      }));
    }
  };

  // Send typing indicator
  const sendTypingIndicator = (isTyping) => {
    if (wsRef.current?.readyState === WebSocket.OPEN && selectedRoom) {
      wsRef.current.send(JSON.stringify({
        type: "typing",
        room_id: selectedRoom.room_id,
        is_typing: isTyping
      }));
    }
  };

  // Handle typing
  const handleTyping = (e) => {
    setNewMessage(e.target.value);
    
    // Send typing indicator
    sendTypingIndicator(true);
    
    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Set timeout to send "stopped typing"
    typingTimeoutRef.current = setTimeout(() => {
      sendTypingIndicator(false);
    }, 1500);
  };

  // Play notification sound
  const playNotificationSound = () => {
    try {
      const audio = new Audio("data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJmvpV9Rhdng6uH/69bG0vD/8ODM2O7/7uLLyNHdysri6ufz/NXE09/P3eLk6f3z2c3W3NDa4eTo8fvw08rZ0tPY2tzg6+/28NLL3c3T0s3T2OHp7/Hs0c/f");
      audio.volume = 0.3;
      audio.play().catch(() => {});
    } catch (e) {}
  };

  // Connect WebSocket when widget opens
  useEffect(() => {
    if (isOpen && user?.user_id && user?.active_org_id) {
      connectWebSocket();
    }
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [isOpen, user?.user_id, user?.active_org_id, connectWebSocket]);

  // Fetch rooms on open
  useEffect(() => {
    if (isOpen) {
      fetchRooms();
      fetchUsers();
    }
  }, [isOpen]);

  // Fetch messages when room selected
  useEffect(() => {
    if (selectedRoom) {
      fetchMessages(selectedRoom.room_id);
      markMessagesAsRead(selectedRoom.room_id);
    }
  }, [selectedRoom?.room_id]);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // Fetch initial unread count
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000); // Less frequent with WebSocket
    return () => clearInterval(interval);
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const response = await fetch(`${API}/chat/unread-count`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.count || 0);
      }
    } catch (error) {}
  };

  const fetchRooms = async () => {
    try {
      const response = await fetch(`${API}/chat/rooms`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setRooms(Array.isArray(data) ? data : (data.rooms || []));
      }
    } catch (error) {}
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API}/users`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        const usersList = Array.isArray(data) ? data : (data.users || data || []);
        setUsers(usersList.filter(u => u.user_id !== user?.user_id));
      }
    } catch (error) {}
  };

  const fetchMessages = async (roomId) => {
    try {
      const response = await fetch(`${API}/chat/rooms/${roomId}/messages`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || []);
      }
    } catch (error) {}
  };

  const selectRoom = (room) => {
    setSelectedRoom(room);
    setShowRoomList(false);
    setTypingUsers({});
  };

  const startDirectMessage = async (targetUser) => {
    try {
      const response = await fetch(`${API}/chat/dm/${targetUser.user_id}`, {
        method: "POST",
        credentials: "include"
      });
      if (response.ok) {
        const room = await response.json();
        setSelectedRoom(room);
        setShowRoomList(false);
        fetchRooms();
      }
    } catch (error) {
      toast.error("Failed to start conversation");
    }
  };

  const formatMessageDate = (dateString) => {
    const date = new Date(dateString);
    if (isToday(date)) return format(date, "h:mm a");
    if (isYesterday(date)) return "Yesterday " + format(date, "h:mm a");
    return format(date, "MMM d, h:mm a");
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const isUserOnline = (userId) => onlineUsers.includes(userId);

  // Render typing indicator
  const renderTypingIndicator = () => {
    const typingUserIds = Object.keys(typingUsers);
    if (typingUserIds.length === 0) return null;
    
    const typingNames = typingUserIds
      .map(id => users.find(u => u.user_id === id)?.name?.split(" ")[0] || "Someone")
      .join(", ");
    
    return (
      <div className="text-xs text-slate-500 italic px-4 py-1">
        {typingNames} {typingUserIds.length === 1 ? "is" : "are"} typing...
      </div>
    );
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 bg-primary text-white p-4 rounded-full shadow-lg hover:bg-primary/90 transition-all z-50"
        data-testid="chat-widget-btn"
      >
        <MessageCircle className="w-6 h-6" />
        {unreadCount > 0 && (
          <Badge className="absolute -top-1 -right-1 bg-red-500 text-white text-xs min-w-[20px] h-5">
            {unreadCount > 99 ? "99+" : unreadCount}
          </Badge>
        )}
      </button>
    );
  }

  if (isMinimized) {
    return (
      <div 
        className="fixed bottom-6 right-6 bg-white border shadow-lg rounded-lg w-64 cursor-pointer z-50"
        onClick={() => setIsMinimized(false)}
        data-testid="chat-minimized"
      >
        <div className="flex items-center justify-between p-3 border-b">
          <div className="flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-primary" />
            <span className="font-medium text-sm">Team Chat</span>
            {wsConnected && <Circle className="w-2 h-2 fill-emerald-500 text-emerald-500" />}
          </div>
          <div className="flex items-center gap-1">
            <Maximize2 className="w-4 h-4 text-slate-400" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="fixed bottom-6 right-6 bg-white border shadow-2xl rounded-lg w-96 h-[500px] flex flex-col z-50"
      data-testid="chat-widget"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b bg-primary text-white rounded-t-lg">
        <div className="flex items-center gap-2">
          {!showRoomList && (
            <button onClick={() => { setShowRoomList(true); setSelectedRoom(null); }}>
              <ChevronLeft className="w-5 h-5" />
            </button>
          )}
          <MessageCircle className="w-5 h-5" />
          <span className="font-medium">
            {selectedRoom ? (selectedRoom.is_team ? "Team Chat" : selectedRoom.name) : "Messages"}
          </span>
          {wsConnected && <Circle className="w-2 h-2 fill-emerald-300 text-emerald-300" title="Connected" />}
        </div>
        <div className="flex items-center gap-1">
          <button 
            onClick={() => setNotificationsEnabled(!notificationsEnabled)}
            className="p-1 hover:bg-white/20 rounded"
            title={notificationsEnabled ? "Mute notifications" : "Enable notifications"}
          >
            {notificationsEnabled ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
          </button>
          <button onClick={() => setIsMinimized(true)} className="p-1 hover:bg-white/20 rounded">
            <Minimize2 className="w-4 h-4" />
          </button>
          <button onClick={() => setIsOpen(false)} className="p-1 hover:bg-white/20 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {showRoomList ? (
          <ScrollArea className="h-full">
            {/* Team Chat */}
            <div className="p-2">
              <p className="text-xs text-slate-500 px-2 py-1 font-medium">Channels</p>
              {rooms.filter(r => r.is_team).map((room) => (
                <button
                  key={room.room_id}
                  onClick={() => selectRoom(room)}
                  className="w-full flex items-center gap-3 p-2 hover:bg-slate-50 rounded-lg transition-colors"
                  data-testid={`room-${room.room_id}`}
                >
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Hash className="w-4 h-4 text-primary" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium">Team Chat</p>
                    {room.last_message && (
                      <p className="text-xs text-slate-500 truncate">{room.last_message}</p>
                    )}
                  </div>
                  {room.unread_count > 0 && (
                    <Badge className="bg-primary text-white">{room.unread_count}</Badge>
                  )}
                </button>
              ))}
            </div>

            {/* Direct Messages */}
            <div className="p-2 border-t">
              <p className="text-xs text-slate-500 px-2 py-1 font-medium">Direct Messages</p>
              {rooms.filter(r => !r.is_team).map((room) => (
                <button
                  key={room.room_id}
                  onClick={() => selectRoom(room)}
                  className="w-full flex items-center gap-3 p-2 hover:bg-slate-50 rounded-lg transition-colors"
                  data-testid={`room-${room.room_id}`}
                >
                  <div className="relative">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={room.picture} />
                      <AvatarFallback className="text-xs">{getInitials(room.name)}</AvatarFallback>
                    </Avatar>
                    {isUserOnline(room.other_user_id) && (
                      <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-emerald-500 border-2 border-white rounded-full" />
                    )}
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium">{room.name}</p>
                    {room.last_message && (
                      <p className="text-xs text-slate-500 truncate">{room.last_message}</p>
                    )}
                  </div>
                  {room.unread_count > 0 && (
                    <Badge className="bg-primary text-white">{room.unread_count}</Badge>
                  )}
                </button>
              ))}

              {/* Start new conversation */}
              <div className="mt-2 pt-2 border-t">
                <p className="text-xs text-slate-500 px-2 py-1">Start a conversation</p>
                {users.slice(0, 5).map((u) => (
                  <button
                    key={u.user_id}
                    onClick={() => startDirectMessage(u)}
                    className="w-full flex items-center gap-3 p-2 hover:bg-slate-50 rounded-lg transition-colors"
                    data-testid={`start-dm-${u.user_id}`}
                  >
                    <div className="relative">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={u.picture} />
                        <AvatarFallback className="text-xs">{getInitials(u.name)}</AvatarFallback>
                      </Avatar>
                      {isUserOnline(u.user_id) && (
                        <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-emerald-500 border-2 border-white rounded-full" />
                      )}
                    </div>
                    <span className="text-sm">{u.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </ScrollArea>
        ) : (
          // Messages view
          <div className="flex flex-col h-full">
            <ScrollArea className="flex-1 p-4">
              <div className="space-y-4">
                {messages.map((msg) => {
                  const isOwn = msg.sender_id === user?.user_id;
                  const isRead = msg.read_by?.length > 1;
                  
                  return (
                    <div
                      key={msg.message_id}
                      className={`flex gap-2 ${isOwn ? "justify-end" : ""}`}
                      data-testid={`message-${msg.message_id}`}
                    >
                      {!isOwn && (
                        <div className="relative">
                          <Avatar className="h-7 w-7">
                            <AvatarImage src={msg.sender_picture} />
                            <AvatarFallback className="text-xs">{getInitials(msg.sender_name)}</AvatarFallback>
                          </Avatar>
                          {isUserOnline(msg.sender_id) && (
                            <span className="absolute bottom-0 right-0 w-2 h-2 bg-emerald-500 border border-white rounded-full" />
                          )}
                        </div>
                      )}
                      <div className={`max-w-[75%] ${isOwn ? "text-right" : ""}`}>
                        {!isOwn && (
                          <p className="text-xs text-slate-500 mb-1">{msg.sender_name}</p>
                        )}
                        <div
                          className={`rounded-lg px-3 py-2 text-sm ${
                            isOwn ? "bg-primary text-white" : "bg-slate-100"
                          }`}
                        >
                          {msg.content}
                        </div>
                        <div className="flex items-center gap-1 mt-0.5">
                          <p className="text-[10px] text-slate-400">
                            {formatMessageDate(msg.sent_at)}
                          </p>
                          {isOwn && (
                            isRead ? (
                              <CheckCheck className="w-3 h-3 text-blue-500" title="Read" />
                            ) : (
                              <Check className="w-3 h-3 text-slate-400" title="Sent" />
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>
            
            {/* Typing indicator */}
            {renderTypingIndicator()}
            
            {/* Message input */}
            <div className="p-3 border-t">
              <div className="flex gap-2">
                <Input
                  value={newMessage}
                  onChange={handleTyping}
                  onKeyPress={(e) => e.key === "Enter" && sendMessage()}
                  placeholder="Type a message..."
                  className="flex-1"
                  data-testid="message-input"
                />
                <Button size="icon" onClick={sendMessage} data-testid="send-message-btn">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
